// Setup: DeepSeek API key + model override + linger. The key persists across
// sessions until cleared: OS keychain in Electron, localStorage in browser dev.
import { useState } from 'react';
import { DEFAULT_GEMINI_MODEL, DEFAULT_MODEL, GEMINI_MAX_KEYS, pingApiKey, type LlmProvider } from '../llm/client.js';
import { isHostedSite } from '../llm/hosted.js';
import TimedNotice from './timedNotice.js';
import { isElectronRuntime, keyStorageLabel, loadPersistedKeys } from './secureStore.js';

export default function SetupScreen(props: {
  apiKeys: string[];
  provider: LlmProvider;
  model: string;
  linger: number;
  onSave: (provider: LlmProvider, keys: string[], model: string, linger: number) => void;
  onClearKey: () => void;
  onBack: () => void;
}): JSX.Element {
  const [keys, setKeys] = useState<string[]>(() =>
    props.provider === 'gemini'
      ? [...props.apiKeys, ...Array(Math.max(0, GEMINI_MAX_KEYS - props.apiKeys.length)).fill('')].slice(0, GEMINI_MAX_KEYS)
      : [props.apiKeys[0] ?? ''],
  );
  const [provider, setProvider] = useState<LlmProvider>(props.provider);
  const [model, setModel] = useState(props.model);
  const [linger, setLinger] = useState(props.linger);
  const [status, setStatus] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(
    props.apiKeys.some((k) => k.trim()) ? `Key${props.apiKeys.filter((k) => k.trim()).length > 1 ? 's' : ''} saved (${keyStorageLabel()}). Saving replaces them.` : null,
  );
  // Per-slot ping status for Gemini (slot index -> message).
  const [slotStatus, setSlotStatus] = useState<Record<number, string>>({});

  const filledCount = keys.filter((k) => k.trim()).length;
  const hosted = isHostedSite();

  // Provider keys are independent. Switching provider loads that provider's
  // saved credentials instead of accidentally showing the other key.
  async function changeProvider(next: LlmProvider): Promise<void> {
    setProvider(next);
    const loaded = await loadPersistedKeys(next);
    setKeys(next === 'gemini'
      ? [...loaded, ...Array(Math.max(0, GEMINI_MAX_KEYS - loaded.length)).fill('')].slice(0, GEMINI_MAX_KEYS)
      : [loaded[0] ?? ''],
    );
    setModel(next === 'gemini' ? DEFAULT_GEMINI_MODEL : DEFAULT_MODEL);
    setStatus(null);
    setSlotStatus({});
  }

  function setSlot(i: number, v: string): void {
    setKeys((ks) => ks.map((k, j) => (j === i ? v : k)));
  }

  async function check(): Promise<void> {
    setChecking(true);
    setStatus(null);
    try {
      // Tiny ping through the shared client (same body shape as game calls).
      const first = keys.find((k) => k.trim()) ?? '';
      const ok = await pingApiKey(first, model.trim() || (provider === 'gemini' ? DEFAULT_GEMINI_MODEL : DEFAULT_MODEL), undefined, provider);
      setStatus(ok ? 'Key works. You are good to start.' : 'Key check failed — invalid key or network issue.');
    } catch (e) {
      setStatus(`Network error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setChecking(false);
    }
  }

  async function checkSlot(i: number): Promise<void> {
    const k = keys[i]?.trim() ?? '';
    if (!k) return;
    setSlotStatus((s) => ({ ...s, [i]: 'checking…' }));
    try {
      const ok = await pingApiKey(k, model.trim() || DEFAULT_GEMINI_MODEL, undefined, 'gemini');
      setSlotStatus((s) => ({ ...s, [i]: ok ? 'works ✓' : 'failed ✗' }));
    } catch (e) {
      setSlotStatus((s) => ({ ...s, [i]: `error: ${e instanceof Error ? e.message : String(e)}` }));
    }
  }

  return (
    <div className="screen">
      <h1>Setup</h1>
      <div className="subtitle">{hosted ? 'DeepSeek is provided for this site.' : 'Bring your own AI provider key. It never leaves your machine.'}</div>
      <div className="field">
        <label htmlFor="provider">Provider</label>
        <select
          id="provider"
          value={provider}
          onChange={(e) => {
            const p = e.target.value as LlmProvider;
            void changeProvider(p);
          }}
        >
          <option value="deepseek">DeepSeek</option>
          <option value="gemini">Google Gemini</option>
        </select>
      </div>
      {!hosted && (
        <div className="field">
          <label htmlFor="api-key">{provider === 'gemini' ? 'Google Gemini API keys (up to 5 — extras are failover)' : 'DeepSeek API key'}</label>
          {provider === 'deepseek' ? (
            <input
              id="api-key"
              type="password"
              placeholder="sk-..."
              value={keys[0] ?? ''}
              onChange={(e) => setSlot(0, e.target.value)}
              autoComplete="off"
            />
          ) : (
            <div className="key-slots">
              {keys.map((k, i) => (
                <div key={i} className="key-slot">
                  <input
                    id={i === 0 ? 'api-key' : undefined}
                    type="password"
                    placeholder={i === 0 ? 'AIza… (required)' : `AIza… (backup ${i})`}
                    value={k}
                    onChange={(e) => setSlot(i, e.target.value)}
                    autoComplete="off"
                    aria-label={`Gemini API key ${i + 1}`}
                  />
                  <button
                    className="btn-ghost key-test"
                    onClick={() => void checkSlot(i)}
                    disabled={!k.trim()}
                    title={`Test key ${i + 1}`}
                  >
                    Test
                  </button>
                  {slotStatus[i] === 'checking…' && (
                    <span className="hint key-status">{slotStatus[i]}</span>
                  )}
                  {slotStatus[i] && slotStatus[i] !== 'checking…' && (
                    <TimedNotice text={slotStatus[i]} className="hint key-status" role="status" as="span" />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="hint">
            Saved automatically on Save, kept across sessions until you clear {provider === 'gemini' && filledCount > 1 ? 'them' : 'it'}. Stored in: {keyStorageLabel()}.
            {!isElectronRuntime() && ' (Dev browsers have no keychain — fine locally, never on shared machines.)'}
            {provider === 'gemini' && ' If a call is refused, the game retries with the next filled key, wrapping back to key 1.'}
          </div>
        </div>
      )}
      <div className="field">
        <label htmlFor="model">Model (default {provider === 'gemini' ? DEFAULT_GEMINI_MODEL : DEFAULT_MODEL})</label>
        <input id="model" value={model} onChange={(e) => setModel(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="linger">
          Chat bubble linger: <strong>{Math.round(linger * 100)}%</strong>
          {linger <= 0.75 ? ' (brisk)' : linger <= 1 ? ' (relaxed)' : ' (leisurely)'}
        </label>
        <input
          id="linger"
          type="range"
          min={0.5}
          max={2}
          step={0.05}
          value={linger}
          onChange={(e) => setLinger(Number(e.target.value))}
        />
        <div className="hint">How long each line stays up after typing. Default 75%.</div>
      </div>
      <div className="menu-row">
        {!hosted && (
          <button className="btn-ghost" onClick={() => void check()} disabled={filledCount === 0 || checking}>
            {checking ? 'Checking…' : provider === 'gemini' ? 'Test First Key' : 'Test Key'}
          </button>
        )}
        <button className="btn-primary" onClick={() => props.onSave(provider, keys.map((k) => k.trim()).filter(Boolean), model.trim() || (provider === 'gemini' ? DEFAULT_GEMINI_MODEL : DEFAULT_MODEL), linger)} disabled={!hosted && filledCount === 0}>
          Save
        </button>
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
      </div>
      {!hosted && (
        <div className="menu-row">
          <button
            className="btn-ghost"
            disabled={filledCount === 0 && !props.apiKeys.some((k) => k.trim())}
            title="Delete the saved keys from this machine"
            onClick={() => {
              if (!window.confirm('Clear the saved API key from this machine? This cannot be undone.')) return;
              setKeys(provider === 'gemini' ? Array(GEMINI_MAX_KEYS).fill('') : ['']);
              setSavedNote(null);
              setSlotStatus({});
              setStatus('Saved keys cleared from this machine.');
              props.onClearKey();
            }}
          >
            Clear saved key{filledCount > 1 ? 's' : ''}
          </button>
        </div>
      )}
      {savedNote && <div className="hint">{savedNote}</div>}
      <TimedNotice text={status} className="hint" role="status" as="div" />
      <div className="hint">Edgy-but-safe content: arguments and uncomfortable topics allowed; no explicit sexual or graphic violent content.</div>
    </div>
  );
}
